import { useEffect, useState } from "react";
import { Plus, X, RotateCw, Play, Trash2 } from "lucide-react";
import api, { formatErr } from "../lib/apiClient";
import { fmtCAD, fmtDate, todayIso } from "../lib/format";

const FREQS = [
  { v: "weekly", l: "Weekly" }, { v: "biweekly", l: "Bi-weekly" },
  { v: "monthly", l: "Monthly" }, { v: "quarterly", l: "Quarterly" },
  { v: "custom", l: "Custom (days)" },
];

export default function RecurringInvoices() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pricebook, setPricebook] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const [{ data: r }, { data: c }, { data: pb }] = await Promise.all([
      api.get("/recurring-invoices"), api.get("/customers"), api.get("/pricebook"),
    ]);
    setItems(r); setCustomers(c); setPricebook(pb);
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({
    customer_id: "",
    frequency: "monthly",
    interval_days: 30,
    next_run: todayIso(),
    active: true,
    send_email: true,
    notes: "",
    invoice_template: {
      payment_terms: "Net 30",
      invoice_discount_type: "amount", invoice_discount_value: 0,
      line_items: [],
    },
  });

  const addLineFromPb = (pb) => {
    setEditing({
      ...editing,
      invoice_template: {
        ...editing.invoice_template,
        line_items: [
          ...editing.invoice_template.line_items,
          { id: crypto.randomUUID(), name: pb.name, description: pb.description, quantity: 1, rate: pb.sale_price, unit: pb.unit, taxable: pb.taxable, line_discount_type: "amount", line_discount_value: 0, pricebook_item_id: pb.id },
        ],
      },
    });
  };

  const removeLine = (i) => {
    const arr = [...editing.invoice_template.line_items]; arr.splice(i, 1);
    setEditing({ ...editing, invoice_template: { ...editing.invoice_template, line_items: arr } });
  };

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = { ...editing };
      if (editing.id) await api.put(`/recurring-invoices/${editing.id}`, payload);
      else await api.post("/recurring-invoices", payload);
      setShowForm(false); setEditing(null); load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/recurring-invoices/${id}`); load(); };
  const runNow = async (id) => { await api.post(`/recurring-invoices/${id}/run-now`); load(); alert("Invoice generated"); };

  return (
    <div data-testid="recurring-page" className="space-y-6">
      <div className="flex justify-between gap-4 flex-wrap">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Recurring Invoices</h1><p className="text-sm text-slate-500 mt-1">Auto-generate invoices on a schedule.</p></div>
        <button onClick={() => { setEditing(blank()); setShowForm(true); }} data-testid="add-recurring-btn" className="self-start px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Plus size={16} /> New Recurring</button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 text-[11px] uppercase">
            <tr><th className="px-4 py-3 text-left">Customer</th><th className="px-4 py-3 text-left">Frequency</th><th className="px-4 py-3 text-left">Next run</th><th className="px-4 py-3 text-left">Active</th><th className="px-4 py-3 text-left">Auto-send</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No recurring invoices.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-3">{customers.find((c) => c.id === r.customer_id)?.name || "—"}</td>
                <td className="px-4 py-3 capitalize">{r.frequency}{r.frequency === "custom" ? ` (${r.interval_days}d)` : ""}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(r.next_run)}</td>
                <td className="px-4 py-3">{r.active ? <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded">Active</span> : <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">Paused</span>}</td>
                <td className="px-4 py-3">{r.send_email ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => runNow(r.id)} className="px-2 py-1 text-xs border border-slate-300 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 mr-1 inline-flex items-center gap-1"><Play size={11} /> Run now</button>
                  <button onClick={() => { setEditing(r); setShowForm(true); }} className="text-xs text-[#0F4C81] hover:underline mr-2">Edit</button>
                  <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white dark:bg-slate-900 rounded-md max-w-3xl w-full p-6 space-y-3 max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center"><h3 className="font-display text-xl font-bold">Recurring Invoice</h3><button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"><X size={18} /></button></div>
            {err && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950 px-3 py-2 rounded">{err}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="text-xs uppercase">Customer *</label>
                <select required value={editing.customer_id} onChange={(e) => setEditing({ ...editing, customer_id: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md">
                  <option value="">— Select —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs uppercase">Frequency</label>
                <select value={editing.frequency} onChange={(e) => setEditing({ ...editing, frequency: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md">
                  {FREQS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
                </select>
              </div>
              {editing.frequency === "custom" && (
                <div><label className="text-xs uppercase">Interval (days)</label><input type="number" value={editing.interval_days || 30} onChange={(e) => setEditing({ ...editing, interval_days: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md tabular" /></div>
              )}
              <div><label className="text-xs uppercase">Next run *</label><input type="date" required value={(editing.next_run || "").slice(0, 10)} onChange={(e) => setEditing({ ...editing, next_run: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md" /></div>
              <div><label className="text-xs uppercase">Payment terms</label>
                <select value={editing.invoice_template.payment_terms} onChange={(e) => setEditing({ ...editing, invoice_template: { ...editing.invoice_template, payment_terms: e.target.value } })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md">
                  {["Due on Receipt", "Net 15", "Net 30", "Net 60"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-span-2 flex gap-4 items-center">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.send_email} onChange={(e) => setEditing({ ...editing, send_email: e.target.checked })} /> Auto-send email on generation</label>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1"><label className="text-xs uppercase">Line items</label>
                <select onChange={(e) => { const pb = pricebook.find((p) => p.id === e.target.value); if (pb) { addLineFromPb(pb); e.target.value = ""; } }} className="text-xs px-2 py-1 border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded">
                  <option value="">+ Add from Price Book...</option>
                  {pricebook.map((p) => <option key={p.id} value={p.id}>{p.name} · {fmtCAD(p.sale_price)}</option>)}
                </select>
              </div>
              <table className="w-full text-sm"><thead className="text-slate-500 text-[11px] uppercase"><tr><th className="text-left">Item</th><th className="text-right w-16">Qty</th><th className="text-right w-24">Rate</th><th></th></tr></thead>
                <tbody>
                  {editing.invoice_template.line_items.map((li, i) => (
                    <tr key={li.id || i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-2">{li.name}</td>
                      <td className="text-right tabular">{li.quantity}</td>
                      <td className="text-right tabular">{fmtCAD(li.rate)}</td>
                      <td className="text-right"><button type="button" onClick={() => removeLine(i)} className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button></td>
                    </tr>
                  ))}
                  {editing.invoice_template.line_items.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-400">No items</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Save</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 dark:border-slate-700 text-sm rounded-md hover:bg-slate-50 dark:hover:bg-slate-800">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
